import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Ghost, Menu, X, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useI18n, LANGS } from '../lib/i18n';
import BrandGhostie from './BrandGhostie';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'What is Ghostie?' },
  { to: '/cities', label: 'Cities' },
  { to: '/blog', label: 'Blog' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/faq', label: 'FAQ' },
  { to: '/contacts', label: 'Contact us!' },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [showBook, setShowBook] = useState(false);
  const { user, logout } = useAuth();
  const { lang, setLang } = useI18n();
  const navigate = useNavigate();

  const dashHref = user
    ? (user.role === 'admin' ? '/admin' : user.role === 'host' ? '/host' : '/dashboard')
    : '/login';

  const handleLogin = () => navigate('/login');

  useEffect(() => {
    const onScroll = () => setShowBook(window.scrollY > Math.max(520, window.innerHeight * 0.7));
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0a0a0a]/80 border-b border-[#3f2e1e]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-xl bg-[#262626] flex items-center justify-center border border-[#3f2e1e]">
              <Ghost size={20} className="text-[#f97316]" />
            </div>
            <BrandGhostie className="font-heading font-semibold text-lg sm:text-xl tracking-tight" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map(n => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g,'-')}`}
                className={({isActive}) => `px-3 py-2 rounded-full text-sm font-semibold transition-colors ${isActive ? 'text-[#f97316]' : 'text-[#a3a3a3] hover:text-white'}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <select value={lang} onChange={(e)=>setLang(e.target.value)} data-testid="lang-selector"
              className="hidden sm:block bg-[#171717] border border-[#3f2e1e] rounded-full px-2 py-1 text-xs font-bold cursor-pointer hover:border-[#f97316] transition-colors">
              {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            {user && user.role !== 'admin' ? (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Link to={dashHref} data-testid="nav-dashboard" className="btn-secondary text-xs sm:text-sm py-2 px-2.5 sm:px-4">
                  {user.role === 'host' ? 'Host' : 'Dashboard'}
                </Link>
                <button onClick={()=>{logout(); navigate('/');}} data-testid="nav-logout" className="hidden sm:inline text-xs text-[#a3a3a3] hover:text-white">Logout</button>
              </div>
            ) : !user ? (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <button onClick={handleLogin} data-testid="nav-login" className="btn-secondary text-xs sm:text-sm py-2 px-2.5 sm:px-4">Login</button>
                <Link to="/signup" data-testid="nav-signup" className="btn-secondary text-xs sm:text-sm py-2 px-2.5 sm:px-4 whitespace-nowrap">Sign Up</Link>
              </div>
            ) : null}
            {showBook && (
              <Link to="/book" data-testid="nav-book-cta" className="hidden md:inline-flex btn-primary text-sm py-2 px-4">
                <Sparkles size={14}/> Book the Experience!
              </Link>
            )}
            <button onClick={()=>setOpen(!open)} className="lg:hidden p-2 rounded-lg bg-[#171717] border border-[#3f2e1e]" data-testid="nav-mobile-toggle">
              {open ? <X size={18}/> : <Menu size={18}/>}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-[#3f2e1e] bg-[#0a0a0a]">
            <div className="px-4 py-3 flex flex-col gap-1">
              {NAV.map(n => (
                <NavLink key={n.to} to={n.to} onClick={()=>setOpen(false)} className={({isActive})=>`px-3 py-2 rounded-lg text-sm font-semibold ${isActive?'bg-[#262626] text-[#f97316]':'text-[#a3a3a3]'}`}>
                  {n.label}
                </NavLink>
              ))}
              {user && user.role !== 'admin' ? (
                <Link to={dashHref} onClick={()=>setOpen(false)} className="px-3 py-2 rounded-lg text-sm font-semibold text-[#f97316]">My Dashboard</Link>
              ) : !user ? (
                <>
                  <button onClick={handleLogin} className="px-3 py-2 rounded-lg text-sm font-semibold text-left text-[#f97316]">Login</button>
                  <Link to="/signup" onClick={()=>setOpen(false)} className="px-3 py-2 rounded-lg text-sm font-semibold text-[#f97316]">Sign Up</Link>
                </>
              ) : null}
              <Link to="/book" onClick={()=>setOpen(false)} className="px-3 py-2 rounded-lg text-sm font-semibold text-[#f97316]">Book the Experience!</Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-[#3f2e1e] mt-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Ghost size={20} className="text-[#f97316]" />
              <BrandGhostie className="font-heading font-semibold text-lg" />
            </div>
            <p className="text-sm text-[#a3a3a3] leading-relaxed">
              Private mystery meetups with vetted local enthusiasts. Not a ghost tour.
            </p>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#f97316] mb-3 font-accent">Explore</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="link-soft">What is Ghostie?</Link></li>
              <li><Link to="/cities" className="link-soft">Cities</Link></li>
              <li><Link to="/blog" className="link-soft">Blog</Link></li>
              <li><Link to="/reviews" className="link-soft">Reviews</Link></li>
              <li><Link to="/faq" className="link-soft">FAQ</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#f97316] mb-3 font-accent">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/contacts" className="link-soft">Contact us!</Link></li>
              <li><Link to="/login" className="link-soft">Login</Link></li>
              <li><Link to="/signup" className="link-soft">Sign Up</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#f97316] mb-3 font-accent">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/legal/terms" className="link-soft">Terms</Link></li>
              <li><Link to="/legal/privacy" className="link-soft">Privacy</Link></li>
              <li><Link to="/legal/safety" className="link-soft">Safety Guidelines</Link></li>
              <li><Link to="/legal/cancellation" className="link-soft">Cancellation</Link></li>
              <li><Link to="/legal/paranormal" className="link-soft">Paranormal Disclaimer</Link></li>
              <li><Link to="/legal/host-guidelines" className="link-soft">Host Guidelines</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#3f2e1e]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 text-xs text-[#a3a3a3] flex flex-wrap items-center justify-between gap-2">
            <span>© {new Date().getFullYear()} Ghostie. We don't guarantee paranormal activity.</span>
            <span>Made with candlelight and curiosity.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
